export type OtlpAttributeValue = string | number | boolean;
export type OtlpAttributes = Record<string, OtlpAttributeValue | null | undefined>;

export type OtlpSpan = {
	name: string;
	traceId: string;
	spanId: string;
	parentSpanId?: string;
	startTimeMs: number;
	endTimeMs: number;
	kind?: OtlpSpanKind;
	isError?: boolean;
	attributes?: OtlpAttributes;
};

export type OtlpLogRecord = {
	timeMs: number;
	severity: OtlpSeverity;
	body: string;
	traceId?: string;
	spanId?: string;
	attributes?: OtlpAttributes;
};

export enum OtlpSpanKind {
	Internal = 1,
	Server = 2,
	Client = 3
}

export enum OtlpSeverity {
	Debug = 5,
	Information = 9,
	Warning = 13,
	Error = 17
}

const severityText: Record<OtlpSeverity, string> = {
	[OtlpSeverity.Debug]: 'Debug',
	[OtlpSeverity.Information]: 'Information',
	[OtlpSeverity.Warning]: 'Warning',
	[OtlpSeverity.Error]: 'Error'
};

export const DEFAULT_HISTOGRAM_BOUNDS_MS = [25, 50, 100, 250, 500, 750, 1000, 1500, 2000, 3000, 5000, 7500, 10000, 15000, 30000];

type HistogramPoint = {
	attributes: OtlpAttributes;
	count: number;
	sum: number;
	min: number;
	max: number;
	bucketCounts: number[];
};

type Histogram = {
	unit: string;
	bounds: number[];
	points: Map<string, HistogramPoint>;
};

export type OtlpExporterOptions = {
	serviceName: string;
	scopeName?: string;
	flushDelayMs?: number;
	resourceAttributes?: OtlpAttributes;
	fetchImpl?: typeof fetch;
	now?: () => number;
};

export type OtlpExporter = {
	readonly baseUrl: string;
	exportSpan(span: OtlpSpan): void;
	exportLog(log: OtlpLogRecord): void;
	recordHistogram(name: string, value: number, attributes?: OtlpAttributes, unit?: string, bounds?: number[]): void;
	flush(): Promise<void>;
};

function toAttributeList(attributes: OtlpAttributes = {}) {
	return Object.entries(attributes)
		.filter(([, value]) => value !== null && value !== undefined && value !== '')
		.map(([key, value]) => {
			if (typeof value === 'boolean')
				return { key, value: { boolValue: value } };
			if (typeof value === 'number')
				return Number.isInteger(value) ? { key, value: { intValue: String(value) } } : { key, value: { doubleValue: value } };
			return { key, value: { stringValue: String(value) } };
		});
}

function toUnixNano(ms: number): string {
	return (BigInt(Math.round(ms * 1000)) * BigInt(1000)).toString();
}

function randomHex(bytes: number): string {
	const values = new Uint8Array(bytes);
	if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function')
		crypto.getRandomValues(values);
	else
		values.forEach((_, index) => (values[index] = Math.floor(Math.random() * 256)));
	return Array.from(values, value => value.toString(16).padStart(2, '0')).join('');
}

export function createTraceId(): string {
	return randomHex(16);
}

export function createSpanId(): string {
	return randomHex(8);
}

function hashToHex(value: string, length: number): string {
	let hex = '';
	for (let seed = 0; hex.length < length; seed++) {
		let hash = 0x811c9dc5 ^ seed;
		for (let index = 0; index < value.length; index++) {
			hash ^= value.charCodeAt(index);
			hash = Math.imul(hash, 0x01000193);
		}
		hex += (hash >>> 0).toString(16).padStart(8, '0');
	}
	hex = hex.slice(0, length);
	return /[1-9a-f]/.test(hex) ? hex : `${hex.slice(0, -1)}1`;
}

/**
 * Normalizes an id to the fixed-width lowercase hex required by OTLP, hashing ids that come from
 * non-W3C sources (e.g. legacy Application Insights `|root.child.` request ids).
 */
export function normalizeOtlpId(id: string | null | undefined, length: 16 | 32): string | undefined {
	if (!id)
		return undefined;
	const lower = id.toLowerCase();
	if (lower.length === length && /^[0-9a-f]+$/.test(lower) && /[1-9a-f]/.test(lower))
		return lower;
	return hashToHex(id, length);
}

function getInstanceId(): string {
	try {
		const key = 'cognito:otlp-instance-id';
		const existing = sessionStorage.getItem(key);
		if (existing)
			return existing;
		const id = randomHex(4);
		sessionStorage.setItem(key, id);
		return id;
	}
	catch {
		return randomHex(4);
	}
}

/**
 * Creates a minimal OTLP/HTTP JSON exporter for browser telemetry. Intended for local development against the
 * Aspire dashboard, which accepts OTLP over HTTP with CORS. Spans, logs, and cumulative histograms are batched
 * and flushed on a short delay.
 */
export function createOtlpExporter(baseUrl: string, options: OtlpExporterOptions): OtlpExporter {
	const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
	const now = options.now ?? (() => Date.now());
	const fetchImpl = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
	const flushDelayMs = options.flushDelayMs ?? 1000;
	const scope = { name: options.scopeName ?? 'cognito.browser' };
	const resource = {
		attributes: toAttributeList({
			'service.name': options.serviceName,
			'service.instance.id': getInstanceId(),
			'telemetry.sdk.language': 'webjs',
			...options.resourceAttributes
		})
	};
	const histogramStartMs = now();

	let spans: OtlpSpan[] = [];
	let logs: OtlpLogRecord[] = [];
	let metricsDirty = false;
	const histograms = new Map<string, Histogram>();
	let timer: ReturnType<typeof setTimeout> | null = null;

	function post(path: string, body: unknown, keepalive: boolean): Promise<void> {
		return fetchImpl(normalizedBaseUrl + path, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
			keepalive
		}).then(() => undefined, () => undefined);
	}

	function buildSpansPayload(batch: OtlpSpan[]) {
		return {
			resourceSpans: [{
				resource,
				scopeSpans: [{
					scope,
					spans: batch.map(span => ({
						traceId: span.traceId,
						spanId: span.spanId,
						parentSpanId: span.parentSpanId ?? '',
						name: span.name,
						kind: span.kind ?? OtlpSpanKind.Internal,
						startTimeUnixNano: toUnixNano(span.startTimeMs),
						endTimeUnixNano: toUnixNano(Math.max(span.endTimeMs, span.startTimeMs)),
						attributes: toAttributeList(span.attributes),
						status: { code: span.isError ? 2 : 0 }
					}))
				}]
			}]
		};
	}

	function buildLogsPayload(batch: OtlpLogRecord[]) {
		return {
			resourceLogs: [{
				resource,
				scopeLogs: [{
					scope,
					logRecords: batch.map(log => ({
						timeUnixNano: toUnixNano(log.timeMs),
						observedTimeUnixNano: toUnixNano(log.timeMs),
						severityNumber: log.severity,
						severityText: severityText[log.severity],
						body: { stringValue: log.body },
						attributes: toAttributeList(log.attributes),
						traceId: log.traceId ?? '',
						spanId: log.spanId ?? ''
					}))
				}]
			}]
		};
	}

	function buildMetricsPayload() {
		const timeUnixNano = toUnixNano(now());
		const startTimeUnixNano = toUnixNano(histogramStartMs);
		return {
			resourceMetrics: [{
				resource,
				scopeMetrics: [{
					scope,
					metrics: Array.from(histograms.entries()).map(([name, histogram]) => ({
						name,
						unit: histogram.unit,
						histogram: {
							aggregationTemporality: 2,
							dataPoints: Array.from(histogram.points.values()).map(point => ({
								attributes: toAttributeList(point.attributes),
								startTimeUnixNano,
								timeUnixNano,
								count: String(point.count),
								sum: point.sum,
								min: point.min,
								max: point.max,
								explicitBounds: histogram.bounds,
								bucketCounts: point.bucketCounts.map(String)
							}))
						}
					}))
				}]
			}]
		};
	}

	function flushBatches(keepalive: boolean): Promise<void> {
		if (timer) {
			clearTimeout(timer);
			timer = null;
		}

		const requests: Promise<void>[] = [];
		if (spans.length) {
			requests.push(post('v1/traces', buildSpansPayload(spans), keepalive));
			spans = [];
		}
		if (logs.length) {
			requests.push(post('v1/logs', buildLogsPayload(logs), keepalive));
			logs = [];
		}
		if (metricsDirty) {
			requests.push(post('v1/metrics', buildMetricsPayload(), keepalive));
			metricsDirty = false;
		}
		return Promise.all(requests).then(() => undefined);
	}

	function flush(): Promise<void> {
		return flushBatches(false);
	}

	function scheduleFlush() {
		if (!timer)
			timer = setTimeout(flush, flushDelayMs);
	}

	if (typeof window !== 'undefined')
		window.addEventListener('pagehide', () => void flushBatches(true));

	return {
		baseUrl: normalizedBaseUrl,
		exportSpan(span) {
			spans.push(span);
			scheduleFlush();
		},
		exportLog(log) {
			logs.push(log);
			scheduleFlush();
		},
		recordHistogram(name, value, attributes = {}, unit = 'ms', bounds = DEFAULT_HISTOGRAM_BOUNDS_MS) {
			if (typeof value !== 'number' || !isFinite(value))
				return;
			let histogram = histograms.get(name);
			if (!histogram) {
				histogram = { unit, bounds, points: new Map() };
				histograms.set(name, histogram);
			}
			const key = JSON.stringify(Object.entries(attributes).sort(([a], [b]) => a.localeCompare(b)));
			let point = histogram.points.get(key);
			if (!point) {
				point = { attributes, count: 0, sum: 0, min: value, max: value, bucketCounts: new Array(histogram.bounds.length + 1).fill(0) };
				histogram.points.set(key, point);
			}
			point.count++;
			point.sum += value;
			point.min = Math.min(point.min, value);
			point.max = Math.max(point.max, value);
			const bucket = histogram.bounds.findIndex(bound => value <= bound);
			point.bucketCounts[bucket === -1 ? histogram.bounds.length : bucket]++;
			metricsDirty = true;
			scheduleFlush();
		},
		flush
	};
}
